import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getSnapshots } from 'apis/SnapshotsAPI';
import { usePublicationContext } from 'components/Publication/PublicationContext';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import {
  useGetSnapshotsGroupedByQuarter,
  useResolveQuarterSnapshots,
} from 'components/View/filters/DataQuarterFilter/customHooks';
import {
  createDefaultDataQuarterSelection,
  createSnapshotsByCommunityMap,
} from 'components/View/filters/DataQuarterFilter/helpers';
import { DataQuarterSelectionType } from 'components/View/filters/DataQuarterFilter/types';
import { getById } from 'utils/helpers/idMap';

export const useDataQuarterSelection = () => {
  const { communityId, competitorId, competitors, isCompetitorsFetched, viewFilters } = useCommunityViewContext();
  const { publication } = usePublicationContext();

  const selectionState: DataQuarterSelectionType = useMemo(() => {
    const defaults = createDefaultDataQuarterSelection();
    const incoming = (viewFilters as any)?.dataQuarterSelection;
    return {
      selectedQuarterSlug: incoming?.selectedQuarterSlug ?? defaults.selectedQuarterSlug,
      includePriorCompetitors: incoming?.includePriorCompetitors ?? defaults.includePriorCompetitors,
    };
  }, [viewFilters]);

  const competitorIds = useMemo(
    () => {
      const contextCompetitorIds = (competitors ?? [])
        .map((competitor: any) => Number(competitor.id))
        .filter((id: number) => !Number.isNaN(id));

      if (!publication?.hash) {
        return contextCompetitorIds;
      }

      const filterCompetitorIds = ((viewFilters as any)?.competitors ?? [])
        .map((id: number | string) => Number(id))
        .filter((id: number) => !Number.isNaN(id));
      const routeCompetitorIds = competitorId && !Number.isNaN(Number(competitorId)) ? [Number(competitorId)] : [];

      return Array.from(new Set([...contextCompetitorIds, ...filterCompetitorIds, ...routeCompetitorIds]));
    },
    [competitorId, competitors, publication?.hash, viewFilters]
  );

  const snapshotQueryParams = {
    community_ids: [communityId, ...competitorIds],
    draft: false,
    has_data: true,
    ...(publication?.hash ? { public_hash: publication.hash } : {}),
  };

  const { data: snapshots = [] } = useQuery({
    queryKey: ['snapshot', 'list', communityId, isCompetitorsFetched, ...competitorIds, 'has-data', publication?.hash],
    queryFn: () => getSnapshots(snapshotQueryParams),
    enabled: Boolean(communityId) && isCompetitorsFetched,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const quarters = useGetSnapshotsGroupedByQuarter(snapshots, communityId);

  const effectiveQuarterSlug = useMemo(() => {
    if (!quarters.length) {
      return null;
    }

    if (
      selectionState.selectedQuarterSlug &&
      quarters.some((quarter) => quarter.slug === selectionState.selectedQuarterSlug)
    ) {
      return selectionState.selectedQuarterSlug;
    }

    return quarters[0].slug ?? null;
  }, [quarters, selectionState.selectedQuarterSlug]);

  const selection = useMemo(() => {
    return {
      selectedQuarterSlug: effectiveQuarterSlug,
      includePriorCompetitors: selectionState.includePriorCompetitors ?? true,
    };
  }, [effectiveQuarterSlug, selectionState.includePriorCompetitors]);

  const isLatestQuarterSelected = useMemo(() => {
    if (!quarters.length) {
      return true;
    }
    if (!selection.selectedQuarterSlug) {
      return true;
    }
    return selection.selectedQuarterSlug === quarters[0].slug;
  }, [quarters, selection.selectedQuarterSlug]);

  const { communityMatch, competitorMatches } = useResolveQuarterSnapshots({
    quarters,
    selectedQuarterSlug: effectiveQuarterSlug,
    competitors: competitorIds.map((id) => ({ id })),
  });

  const snapshotsByCommunity = useMemo(
    () =>
      createSnapshotsByCommunityMap(selection, { communityMatch, competitorMatches }, { communityId, competitorIds }),
    [selection, communityMatch, competitorMatches, communityId, competitorIds]
  );

  const snapshotMetaByCommunity = useMemo(() => {
    const formatSnapshotDate = (date?: any) => {
      if (!date) return null;
      if (typeof date === 'string') return date;
      if (typeof date?.toISOString === 'function') {
        return date.toISOString();
      }
      return date?.toString?.() ?? null;
    };

    type SnapshotMeta = { date: string | null; quarterSlug: string | null };
    const map: Record<number, SnapshotMeta> = {};

    if (communityId) {
      const hasSnapshot = Boolean(getById(snapshotsByCommunity, communityId));
      map[communityId] = hasSnapshot
        ? { date: formatSnapshotDate(communityMatch?.snapshot?.date), quarterSlug: communityMatch?.quarterSlug ?? null }
        : { date: null, quarterSlug: null };
    }

    competitorIds.forEach((competitorId) => {
      const hasSnapshot = Boolean(getById(snapshotsByCommunity, competitorId));
      const match = competitorMatches.find((candidate) => candidate.competitorId === competitorId);
      map[competitorId] = hasSnapshot
        ? { date: formatSnapshotDate(match?.snapshot?.date), quarterSlug: match?.quarterSlug ?? null }
        : { date: null, quarterSlug: null };
    });

    return map;
  }, [snapshotsByCommunity, communityMatch, competitorMatches, communityId, competitorIds, selection.selectedQuarterSlug]);

  const snapshotUuids = useMemo(() => {
    const uuids = Object.values(snapshotsByCommunity).filter((uuid): uuid is string => Boolean(uuid));
    return uuids.length ? uuids : null;
  }, [snapshotsByCommunity]);

  const overtimeSnapshotUuids = useMemo(() => {
    if (!quarters.length || !effectiveQuarterSlug) {
      return null;
    }

    const selectedQuarterIndex = quarters.findIndex((q) => q.slug === effectiveQuarterSlug);
    if (selectedQuarterIndex === -1) {
      return null;
    }

    const historicalQuarters = quarters.slice(selectedQuarterIndex);

    const uuids: string[] = [];
    historicalQuarters.forEach((quarter) => {
      if (quarter.communitySnapshot?.uuid) {
        uuids.push(quarter.communitySnapshot.uuid);
      }
      quarter.competitorSnapshots.forEach((snapshot) => {
        if (snapshot.uuid) {
          uuids.push(snapshot.uuid);
        }
      });
    });

    return uuids.length ? uuids : null;
  }, [quarters, effectiveQuarterSlug]);

  const hasSnapshotOverrides = Boolean(snapshotUuids?.length);

  return {
    snapshotsByCommunity,
    snapshotUuids,
    overtimeSnapshotUuids,
    hasSnapshotOverrides,
    snapshotMetaByCommunity,
    selection,
    isLatestQuarterSelected,
    quarters,
  };
};

export default useDataQuarterSelection;
